/**
 * components/actionButton.tsx
 * The design's page-header action button (34px; primary accent / secondary
 * outline / success / danger), with hover feedback. Shared across screens.
 */
import React from 'react';
import { Text, Pressable, ActivityIndicator } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useHover } from '../hooks/useHover';
import { web } from '../utils/web';
import Icon from './icon';

export type ActionVariant = 'primary' | 'secondary' | 'success' | 'danger';

export interface ActionButtonProps {
  label: string;
  onPress?: () => void;
  variant?: ActionVariant;
  icon?: string;
  disabled?: boolean;
  loading?: boolean;
  size?: 'sm' | 'md';
  /** Fill the parent's width (label stays centered). For full-width mobile rows. */
  fullWidth?: boolean;
}

export default function ActionButton({
  label, onPress, variant = 'primary', icon, disabled, loading, size = 'md', fullWidth = false,
}: ActionButtonProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;
  const { hovered, hoverProps } = useHover();

  const solid = variant === 'primary' || variant === 'success' || variant === 'danger';
  const baseColor = variant === 'primary' ? colors.accent : variant === 'success' ? colors.success : variant === 'danger' ? colors.danger : colors.card;
  const fg = solid ? colors.onAccent : colors.ink;
  const borderColor = solid ? baseColor : colors.line;
  const height = size === 'sm' ? 30 : 34;

  return (
    <Pressable
      {...hoverProps}
      onPress={disabled || loading ? undefined : onPress}
      disabled={disabled || loading}
      style={[
        {
          flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7,
          height, paddingHorizontal: 14, borderRadius: radii.sm, borderWidth: 1,
          backgroundColor: baseColor, borderColor,
          opacity: disabled ? 0.5 : 1,
        },
        // Fill the parent (so the centered content sits dead-centre of the full
        // width) — `width:100%` guarantees stretch even where align-items doesn't.
        fullWidth ? ({ alignSelf: 'stretch', width: '100%' } as object) : null,
        web({ cursor: disabled || loading ? 'default' : 'pointer', transition: 'opacity 120ms ease, background 120ms ease' }),
        !disabled && hovered && web(solid ? { opacity: 0.92 } : { backgroundColor: colors.paper2 }),
      ]}
    >
      {loading ? (
        <ActivityIndicator size="small" color={fg} />
      ) : (
        <>
          {icon && <Icon name={icon} size={14} color={fg} />}
          <Text
            numberOfLines={1}
            style={[
              { fontSize: fontSize.md, fontWeight: fontWeight.medium, color: fg, flexShrink: 1 },
              // Text-only buttons: let the label fill the width and self-center so
              // a short label (e.g. "Jobs →") sits dead-centre when the button is
              // stretched full-width. Icon buttons keep the grouped centering.
              !icon && { flex: 1, textAlign: 'center' },
            ]}
          >{label}</Text>
        </>
      )}
    </Pressable>
  );
}
